<script setup lang="ts">
import { computed, watch } from 'vue'

import type {
  InsightImpactFormat,
  SummaryInsightItem,
  SummaryInsightsState,
} from '../../models/summaryInsight.types'

import { useI18n } from '@/shared/i18n'
import { cur, useFormat } from '@/shared/lib/format'
import { Button } from '@/shared/ui/button'
import { Icon } from '@/shared/ui/icon'
import { PlatformLogo, resolveStaticPlatformLogo } from '@/shared/ui/logo'
import { Skeleton } from '@/shared/ui/skeleton'

const props = withDefaults(
  defineProps<{
    state: SummaryInsightsState
    insights?: readonly SummaryInsightItem[]
    total?: number
    title?: string
    limit?: number
    currency?: string
  }>(),
  {
    insights: () => [],
    limit: 3,
  },
)

const emit = defineEmits<{
  'open-all': []
  open: [insight: SummaryInsightItem]
  retry: []
  count: [count: number]
}>()

const { t } = useI18n()
const format = props.currency ? useFormat({ currency: props.currency }) : useFormat()
const visibleInsights = computed(() => props.insights.slice(0, props.limit))
const total = computed(() => props.total ?? props.insights.length)
const isVisible = computed(
  () =>
    props.state === 'loading' ||
    props.state === 'error' ||
    (props.state === 'ready' && visibleInsights.value.length > 0),
)

watch(
  () => [props.state, total.value] as const,
  ([state, count]) => {
    if (state === 'ready' || state === 'empty') emit('count', count)
  },
  { immediate: true },
)

function formatImpact(value: number, impactFormat: InsightImpactFormat | undefined): string {
  if (impactFormat === 'currency') {
    if (!props.currency) return format.formatCurrency(value, { maxFrac: 0 })

    const amount = format.formatNumber(value, { maxFrac: 0, heavyDefault: true })
    return `${amount}${cur.resolveCurrencySymbol(props.currency, format.locale)}`
  }
  if (impactFormat === 'percentage') {
    return format.formatPercent(value, { scale: 'percent', maxFrac: 1 })
  }
  return format.formatNumber(value, { maxFrac: 0, heavyDefault: true })
}

function platformLogo(item: SummaryInsightItem): { src: string } | Record<string, never> {
  if (!item.platform) return {}
  const src = resolveStaticPlatformLogo(item.platform.key)
  return src ? { src } : {}
}
</script>

<template>
  <section
    v-if="isVisible"
    class="summary-insights w-full overflow-hidden p-md rounded-md bg-surface shadow-card font-sans"
    data-testid="summary-insights-teaser"
    :aria-busy="state === 'loading' || undefined"
  >
    <header class="summary-insights__header flex items-center justify-between gap-sm min-h-6 mb-md">
      <h2
        class="summary-insights__heading flex items-center gap-sm m-0 text-primary text-base font-semibold leading-6"
      >
        {{ title || t('insights.teaser.title') }}
        <span
          class="summary-insights__count relative z-0 grid w-5 h-5 place-items-center rounded-full bg-(image:--ui-gradient-brand) text-primary-fg text-[0.625rem] font-bold leading-3.75 [&::before]:absolute [&::before]:z-[-1] [&::before]:w-7 [&::before]:h-7 [&::before]:border-2 [&::before]:border-accent [&::before]:rounded-[inherit] [&::before]:content-[''] [&::before]:opacity-50"
          >{{ total }}</span
        >
      </h2>
      <button
        class="summary-insights__see-all flex items-center gap-sm text-info text-xs font-bold no-underline"
        type="button"
        @click="emit('open-all')"
      >
        <span>{{ t('insights.teaser.seeAll') }}</span>
        <Icon name="chevron-right" :size="10" />
      </button>
    </header>

    <div v-if="state === 'loading'" class="summary-insights__list grid gap-2.5" aria-live="polite">
      <article
        v-for="item in limit"
        :key="item"
        class="summary-insights__skeleton-card w-full p-3 border border-insight-border rounded-md bg-surface grid gap-xs min-h-31"
      >
        <Skeleton width="80%" height="0.9rem" />
        <Skeleton width="58%" height="0.9rem" />
        <div class="summary-insights__skeleton-meta flex items-center gap-xs mt-2xs">
          <Skeleton width="2.8rem" height="1.25rem" />
          <Skeleton width="5.5rem" height="1.25rem" />
          <Skeleton width="4rem" height="0.75rem" />
        </div>
      </article>
    </div>

    <div
      v-else-if="state === 'error'"
      class="summary-insights__error flex items-center justify-between gap-sm p-sm rounded-md bg-danger-soft text-danger text-[0.8125rem]"
      role="alert"
    >
      <span>{{ t('insights.error.load') }}</span>
      <Button variant="outline" size="xs" @click="emit('retry')">
        {{ t('insights.error.retry') }}
      </Button>
    </div>

    <div v-else class="summary-insights__list grid gap-2.5">
      <button
        v-for="item in visibleInsights"
        :key="item.id"
        type="button"
        class="summary-insights__card w-full p-3 border border-insight-border rounded-md bg-surface text-inherit [font:inherit] text-left cursor-pointer transition-[border-color,box-shadow] duration-150 ease-[ease] [&:hover]:border-insight-hover-border [&:hover]:shadow-insight-hover [&:hover]:outline-none [&:focus-visible]:border-insight-hover-border [&:focus-visible]:shadow-insight-hover [&:focus-visible]:outline-none"
        :aria-label="item.title"
        @click="emit('open', item)"
      >
        <span class="summary-insights__title-row flex items-center items-start gap-xs">
          <span
            v-if="item.platform"
            class="summary-insights__platform grid w-6 h-6 flex-none place-items-center mt-0.5 border border-insight-platform-border rounded-full bg-surface shadow-insight-platform"
            :title="item.platform.label"
          >
            <PlatformLogo
              :platform="item.platform.label"
              v-bind="platformLogo(item)"
              :alt="item.platform.label"
              :size="14"
            />
          </span>
          <span
            class="summary-insights__title min-w-0 overflow-hidden text-fg text-[0.9375rem] font-semibold leading-[1.3] line-clamp-2"
            >{{ item.title }}</span
          >
          <Icon
            class="summary-insights__arrow flex-none mt-1 ml-auto text-subtle transition-[color] duration-150 ease-[ease]"
            name="arrow-right"
            :size="10"
          />
        </span>

        <span
          class="summary-insights__meta flex items-center flex-wrap gap-xs mt-sm text-muted text-[0.625rem] leading-4"
        >
          <span
            v-if="item.isNew"
            class="summary-insights__new rounded-[0.375rem] whitespace-nowrap py-0.5 px-2.5 border border-orpheus text-orpheus font-semibold"
          >
            {{ t('insights.card.new') }}
          </span>
          <span
            class="summary-insights__outcome rounded-[0.375rem] whitespace-nowrap py-0.5 px-2.5 font-medium"
            :class="`summary-insights__tone--${item.outcomeTone}`"
          >
            {{ t(item.outcomeLabelKey) }}
          </span>
          <span
            v-if="item.createdAt"
            class="summary-insights__created flex items-center gap-2xs font-light capitalize"
          >
            <Icon name="clock" weight="regular" :size="10" />
            {{ format.formatRelative(item.createdAt) }}
          </span>
          <span v-if="item.impactMetric" class="summary-insights__metric font-medium">
            {{ item.impactMetric }}
          </span>
          <span
            v-if="item.impactScore !== undefined"
            class="summary-insights__impact rounded-[0.375rem] whitespace-nowrap py-1 px-2.5 text-sm font-semibold leading-none"
            :class="`summary-insights__tone--${item.outcomeTone}`"
          >
            {{ formatImpact(item.impactScore, item.impactFormat) }}
          </span>
        </span>
      </button>
    </div>
  </section>
</template>

<style scoped>
.summary-insights .summary-insights__card:hover .summary-insights__arrow {
  color: var(--ui-color-info);
}
.summary-insights .summary-insights__card:focus-visible .summary-insights__arrow {
  color: var(--ui-color-info);
}
.summary-insights :deep(.summary-insights__tone--neutral) {
  background-color: var(--ui-color-surface-sunken);
  color: var(--ui-color-muted);
}
.summary-insights :deep(.summary-insights__tone--info) {
  background-color: var(--ui-color-info-soft);
  color: var(--ui-color-info);
}
.summary-insights :deep(.summary-insights__tone--success) {
  background-color: var(--ui-color-insight-positive-soft);
  color: var(--ui-color-insight-positive);
}
.summary-insights :deep(.summary-insights__tone--warning) {
  background-color: var(--ui-color-warning-soft);
  color: var(--ui-color-warning);
}
.summary-insights :deep(.summary-insights__tone--danger) {
  background-color: var(--ui-color-danger-soft);
  color: var(--ui-color-danger);
}
</style>
